import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, ArrowRight, Star, CalendarDays } from "lucide-react";
import { inr } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function SectionHeading({ eyebrow, title, subtitle, center }) {
  return (
    <div className={`mb-10 ${center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}`}>
      {eyebrow && <p className="text-xs uppercase tracking-[0.25em] font-semibold text-accent mb-3">{eyebrow}</p>}
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight">{title}</h2>
      {subtitle && <p className="mt-3 text-muted-foreground leading-relaxed">{subtitle}</p>}
    </div>
  );
}

export function MandirCard({ m }) {
  return (
    <Link to={`/mandirs/${m.id}`} data-testid={`mandir-card-${m.id}`}
      className="group rounded-2xl bg-card border border-border hover:border-accent overflow-hidden shadow-sm hover:shadow-lg hover-lift block">
      <div className="relative h-52 overflow-hidden">
        <img src={m.image} alt={m.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <span className="absolute top-3 left-3 text-[11px] font-medium px-2.5 py-1 rounded-full bg-primary text-primary-foreground">{m.deity}</span>
        <div className="absolute bottom-3 left-4 right-4 text-white">
          <h3 className="font-serif text-xl font-semibold leading-tight">{m.name}</h3>
        </div>
      </div>
      <div className="p-5 space-y-3">
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4 text-accent" /> {m.city}, {m.state}</p>
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><Clock className="h-4 w-4 text-accent" /> {m.timings.split(" (")[0]}</p>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all">Explore Mandir <ArrowRight className="h-4 w-4" /></span>
      </div>
    </Link>
  );
}

export function ServiceCard({ s, mandirId }) {
  const bookHref = `/book/${s.type}?service=${s.id}${mandirId ? `&mandir=${mandirId}` : ""}`;
  return (
    <div data-testid={`service-card-${s.id}`} className="group rounded-2xl bg-card border border-border hover:border-accent overflow-hidden shadow-sm hover:shadow-lg hover-lift flex flex-col">
      <div className="relative h-44 overflow-hidden">
        <img src={s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <span className="absolute top-3 left-3 text-[11px] font-medium px-2.5 py-1 rounded-full bg-background/90 text-foreground capitalize">{s.type}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs text-accent font-medium">{s.deity}</p>
        <h3 className="font-serif text-lg font-semibold mt-1 leading-tight">{s.name}</h3>
        <p className="text-sm text-muted-foreground mt-2 line-clamp-2 flex-1">{s.description}</p>
        <div className="flex items-center justify-between pt-4 mt-3 border-t border-border">
          <span className="font-serif text-lg font-bold text-primary">{inr(s.price)}</span>
          <Link to={bookHref} data-testid={`book-now-${s.id}`}>
            <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-9 text-sm">Book Now</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export function MuhuratCard({ item, kind = "festival" }) {
  return (
    <div data-testid={`muhurat-${kind}-card-${item.id}`}
      className="group rounded-2xl bg-card border border-border hover:border-accent overflow-hidden shadow-sm hover:shadow-lg hover-lift flex flex-col">
      <div className="relative h-44 overflow-hidden">
        <img src={item.image} alt={item.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <span className="absolute top-3 left-3 text-[11px] font-medium px-2.5 py-1 rounded-full bg-accent/90 text-white">{item.type}</span>
        <span className="absolute bottom-3 left-3 text-xs font-medium px-2.5 py-1 rounded-full bg-background/90 text-foreground flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5 text-accent" /> {new Date(item.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-serif text-lg font-semibold leading-tight">{item.name}</h3>
        <p className="text-sm text-muted-foreground mt-2 line-clamp-3 flex-1">{item.brief}</p>
        <Link to={`/vedic-services/panchang/${kind}/${item.id}`} data-testid={`muhurat-${kind}-knowmore-${item.id}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-4 group-hover:gap-2.5 transition-all">
          Know more <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

export function TestimonialCard({ t }) {
  return (
    <div data-testid={`testimonial-${t.id}`} className="rounded-2xl bg-card border border-border p-6">
      <div className="flex gap-0.5 mb-3">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-accent text-accent" />)}</div>
      <p className="text-sm text-muted-foreground leading-relaxed">"{t.text}"</p>
      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border">
        <img src={t.image} alt="" className="h-9 w-9 rounded-full object-cover" />
        <div><p className="font-semibold text-sm">{t.name}</p><p className="text-xs text-muted-foreground">{t.city}</p></div>
      </div>
    </div>
  );
}

export function TestimonialsSection({ testimonials = [], eyebrow = "Bhakton ke Vichaar", title = "What devotees say",
  subtitle = "Real experiences from devotees who booked worships, rituals and Vedic services with Pavitra Mantra." }) {
  if (!testimonials.length) return null;
  return (
    <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20" data-testid="testimonials-section">
      <SectionHeading center eyebrow={eyebrow} title={title} subtitle={subtitle} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {testimonials.map((t) => <TestimonialCard key={t.id} t={t} />)}
      </div>
    </section>
  );
}

export function StatsSection({ stats, eyebrow = "Devotion, Quantified", title = "Faith, measured in seva",
  subtitle = "Behind every number is a devotee whose prayers reached the divine. Here is the trust our community places in Pavitra Mantra." }) {
  if (!stats) return null;
  const items = [
    ["Mandirs", (stats.mandirs || 0) + "+"],
    ["Verified Purohits", (stats.purohits || 0) + "+"],
    ["Bookings Served", ((stats.bookings || 0) / 1000).toFixed(0) + "K+"],
    ["Cities", stats.cities],
  ];
  return (
    <section className="bg-secondary/40 border-y border-border" data-testid="stats-section">
      <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-16">
        <SectionHeading center eyebrow={eyebrow} title={title} subtitle={subtitle} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center mt-4">
          {items.map(([l, v]) => (
            <div key={l} data-testid={`stat-${l.toLowerCase().split(" ")[0]}`}>
              <p className="font-serif text-4xl font-bold text-primary">{v}</p>
              <p className="text-sm text-muted-foreground mt-1">{l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
